import { createFormulaReadContext } from './formula-read-context';
import type { ItemFieldRow } from './item-field-reader';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { EQUIPMENT_SLOTS } from '../inventory/equipment-slots';
import { equal } from '../php-compat';
import { constructInventoryDefinitionShells } from './from-base-inventory-references';
import { normalizeFromBaseCatalog, FROM_BASE_STATIC_REGISTRIES } from './from-base-catalog-normalization';
import type { FromBaseRow } from './from-base-context';

export interface EnchantmentEligibilityFacts {
    members: readonly Readonly<Record<string, unknown>>[];
    definitions: Readonly<Record<string, ItemFieldRow | null>>;
    catalogRows: ProviderCatalogRows & Readonly<Record<string, { aggregation_strategy?: string;
        references_item_type?: string; full_nest?: boolean; is_multiple?: boolean }>>;
}
const uuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/** Legacy validate_enchantment, over authored SELECT facts. Preserve its ordered
 * non-armor checks, armor OR logic and inclusion of deleted rows in template existence.
 */
export function validateEnchantment(itemId: string, facts: EnchantmentEligibilityFacts): true | string {
    if (!facts.members.length) return 'This is not even an enchantment_template_enchantment! It must be a valid single enchantment or mix of enchantments!';
    const rawDefinition = (id: string) => (facts.definitions[id] ?? facts.definitions[id.toUpperCase()] ?? null) as FromBaseRow | null;
    const armorKeys = ['enchantment_armor_type', 'enchantment_armor_classification',
        'enchantment_not_armor_type', 'enchantment_not_armor_classification'];
    const armorCatalog = Object.fromEntries(armorKeys.map(key => [key, facts.catalogRows[key] ?? {}]));
    const shellRegistries = Object.fromEntries(Object.values(armorCatalog).flatMap(row => row.references_item_type
        ? [[row.references_item_type, FROM_BASE_STATIC_REGISTRIES[row.references_item_type] ?? null]] : []));
    const definitions = new Map<string, FromBaseRow | null>();
    const definition = (id: string): FromBaseRow | null => {
        const key = id.toUpperCase();
        if (definitions.has(key)) return definitions.get(key)!;
        const raw = rawDefinition(id);
        if (!raw) { definitions.set(key, null); return null; }
        const rawLayers = raw.item_layer_types as FromBaseRow | undefined;
        // Native query_full_item_arr expands these four references before eligibility.
        // Its armor loops compare the resulting cells verbatim: classification shells
        // and static armor-type UUIDs must retain that distinct behavior.
        const source = { items: raw.items, item_layer_types: Object.fromEntries(armorKeys
            .filter(name => rawLayers && Object.prototype.hasOwnProperty.call(rawLayers, name))
            .map(name => [name, rawLayers![name]])) };
        const shells = constructInventoryDefinitionShells(source, { layerCatalog: armorCatalog,
            shellRegistries, shellRegistriesComplete: true, sizeCatalog: [] }, rawDefinition);
        const normalized = normalizeFromBaseCatalog(shells, { definition: rawDefinition, rawDefinition, abilityTypes: [] });
        const result = { ...raw, item_layer_types: { ...rawLayers, ...normalized.item_layer_types as FromBaseRow } };
        definitions.set(key, result); return result;
    };
    const read = createFormulaReadContext({ catalogRows: facts.catalogRows, abilityTypes: [],
        aggregationStrategy: key => facts.catalogRows[key]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: definition, resolveInventoryInstance: () => null });
    const base = definition(itemId);
    const conditions: readonly (readonly [string, unknown, boolean, string])[] = [
        ['item_type', read.itemField(base, 'item_type'), false, 'Item type'],
        ['not_item_type', read.itemField(base, 'item_type'), true, 'Item type'],
        ['equipment_slot', read.layerValue(base, 'equipment_slot') ?? EQUIPMENT_SLOTS.off_hand, false, 'Equipment slot'],
        ['not_equipment_slot', read.layerValue(base, 'equipment_slot') ?? EQUIPMENT_SLOTS.off_hand, true, 'Equipment slot'],
        ...(['weapon_classification', 'weapon_type', 'weapon_category', 'instrument_classification'] as const).flatMap(key => {
            const label = key.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
            return [[key, read.layerValue(base, key), false, label], ['not_' + key, read.layerValue(base, key), true, label]] as const;
        }),
    ];
    for (const member of facts.members) {
        if (Number(member.enchantment_template_enchantment_deleted ?? 0) !== 0 || !uuid(member.enchantment_item_id)) continue;
        const part = definition(member.enchantment_item_id);
        if (!part) continue;
        let valid = true;
        const reasons: string[] = [];
        for (const [key, value, excluded, label] of conditions) {
            const entries = Object.values(read.layerEntries(part, 'enchantment_' + key));
            if (!entries.length) continue;
            const match = entries.some(entry => equal(typeof entry === 'object' && entry !== null
                ? readLayerListEntryUuid(entry) || entry : entry, value));
            valid = excluded ? !match : match;
            if (!valid) reasons.push(label + (excluded ? ' is specifically excluded.' : ' does not match.'));
        }
        const armorReasons: string[] = [];
        const armor = ['armor_type', 'armor_classification'].map(key => {
            const label = key === 'armor_type' ? 'Armor type' : 'Armor classification';
            const allowed = Object.values(read.layerEntries(part, 'enchantment_' + key));
            let fits = !allowed.length || allowed.some(entry => equal(entry, read.layerValue(base, key)));
            if (!fits) armorReasons.push(label + ' does not match.');
            const denied = Object.values(read.layerEntries(part, 'enchantment_not_' + key));
            if (denied.some(entry => equal(entry, read.layerValue(base, key)))) {
                fits = false; armorReasons.push(label + ' is specifically excluded.');
            }
            return fits;
        });
        // PHP records inclusion reasons for both axes before exclusion reasons.
        armorReasons.sort((a, b) => Number(a.includes('excluded')) - Number(b.includes('excluded')));
        if (armor.every(value => !value)) { valid = false; reasons.push(...armorReasons); }
        if (!valid) return 'Enchantment is not VALID for this item: ' + reasons.join('; ');
    }
    return true;
}
